import { createContext, useContext, type ReactNode } from 'react';
import { Badge } from '@/ui-kit/atoms/badge';
import { Button } from '@/ui-kit/atoms/button';
import { FieldLabel } from './field-label';
import { maskLabel, showsMaskedValue, type AccessMap } from './access-map';

/**
 * Shared block primitives for the customer-family detail pages.
 *
 * Extracted from `customer-summary-tab.tsx` (#915) so the order detail page
 * (#859) renders the same visual language rather than a second, divergent
 * block style. Both files previously defined a local `Section`; the order
 * page's was a blue-header-bar variant that is now retired.
 *
 * `Field` emits bare `<dt>`/`<dd>`, so it must always have a `<dl>` ancestor —
 * supplied by `Section` unless `bare` is set.
 */
/**
 * Header row of a block: title on the left, Edit on the right, on a light
 * tint. Exported for the hand-rolled headers in the summary tab (Commission,
 * Bank details) so they cannot drift from `Section`'s. The negative margin
 * pulls the tint out past the text, so the title stays aligned with the
 * fields below it.
 */
export const SECTION_HEADER_CLASS =
  'mb-2 -mx-2 flex items-center justify-between rounded bg-gray-50 px-2 py-1';

export function Section({
  title, canEdit, onEdit, editLabel = 'Edit', bare = false, dxVersion, children,
}: {
  title: string;
  canEdit?: boolean;
  onEdit?: () => void;
  /**
   * Optimistic-locking version of the entity this section edits, shown as a
   * `v{n}` badge beside the heading (#915).
   *
   * Only meaningful for sections whose entity is NOT the page's main entity —
   * that row's own dx_version is already in the page header, so repeating it
   * per section would be noise. An operator reconciling a 409 needs to know
   * WHICH version the message refers to.
   */
  dxVersion?: number | null;
  /** Label for the section's edit button (default "Edit"). */
  editLabel?: string;
  /**
   * Render `children` directly instead of wrapping them in the standard
   * `<dl>` (default false). For sections that supply their own field
   * containers — e.g. the General block (#915), which uses two sibling
   * `<dl>`s to get vertical-first ordering across two columns. `<Field>`
   * emits bare `<dt>`/`<dd>`, so a `bare` section MUST provide its own
   * `<dl>` ancestor for them.
   */
  bare?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-5 break-inside-avoid">
      <div className={SECTION_HEADER_CLASS}>
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-900">{title}</h3>
          {dxVersion != null && (
            <span title="Optimistic locking version of this section's record">
              <Badge tone="info">v{dxVersion}</Badge>
            </span>
          )}
        </div>
        {canEdit && onEdit && (
          <Button size="sm" variant="ghost" onClick={onEdit}>{editLabel}</Button>
        )}
      </div>
      {bare ? children : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
          {children}
        </dl>
      )}
    </div>
  );
}

/**
 * The `_access` map covering the fields inside this block.
 *
 * Context rather than a prop because `Field` is used at ~128 call sites and
 * threading a map through every one of them would be a large, error-prone
 * change for a value that is constant per block.
 *
 * ⚠️ The map belongs to the ENTITY THE BLOCK READS, not to the page's main
 * entity. Bank details reads `app.customer_accounts`, so it must supply that
 * row's map — supplying the customer's would find no entry for those fields,
 * and an absent entry means "no restriction", so every withheld value would
 * silently render as if it were merely empty. Same entity-ownership trap that
 * already caught the bank-details Edit affordance.
 */
const SectionAccessContext = createContext<AccessMap | undefined>(undefined);

/**
 * Supply the `_access` map for every `Field` inside.
 *
 * Blocks without one keep today's behaviour exactly: no provider means no map,
 * and `Field` falls back to the empty-value dash. Adoption is therefore
 * per-block and nothing breaks while a block is unconverted.
 */
export function SectionAccess({ access, children }: { access: AccessMap | undefined; children: ReactNode }) {
  return <SectionAccessContext.Provider value={access}>{children}</SectionAccessContext.Provider>;
}

export function Field({ label, field, children }: { label: string; field?: string; children: ReactNode }) {
  const access = useContext(SectionAccessContext);
  const entry = field ? access?.[field] : undefined;

  // ⚠️ MASKED is checked BEFORE emptiness, and that order is the whole point.
  //
  // `maskRow` nulls a withheld value server-side, so a masked field arrives
  // looking exactly like an empty one — `children` is falsy either way. Testing
  // emptiness first would send every masked field down the `—` branch and the
  // distinction would never render, which is what happened until 2026-08-18:
  // the mask helpers existed, were tested, and had no callers.
  //
  // "Withheld from you" and "nobody filled this in" are different facts and an
  // operator acts differently on each — ask someone with the grant, versus go
  // and fill it in.
  //
  // A value served in its masked form (`last4`) is not withheld: it renders as
  // sent below, and an empty one as `—`.
  if (entry?.masked && !showsMaskedValue(entry)) {
    return (
      <>
        <dt className="text-gray-500"><FieldLabel name={field}>{label}</FieldLabel></dt>
        <dd className="text-gray-400" title="Hidden — your role may not view this field">
          {maskLabel(entry)}
        </dd>
      </>
    );
  }

  return (
    <>
      <dt className="text-gray-500"><FieldLabel name={field}>{label}</FieldLabel></dt>
      <dd className="text-gray-900">{children || <span className="text-gray-300">—</span>}</dd>
    </>
  );
}
